<template>
  <section class="customers-former">
    <state-messages
      key="customer-states"
      :state="customerState"
    >
      <template v-slot:loading>
        Carregando cliente
      </template>

      <template v-slot:error>
        Algo deu errado enquanto tentávamos carregar este cliente.
        Por favor, tente novamente.
      </template>
    </state-messages>

    <section
      v-if="conditions.length && customerState === 'ready'"
      class="customers-former-conditions-container"
    >
      <typography-heading
        class="customers-former-conditions-header"
        @click.native="toggle"
      >
        Condições

        <font-awesome-icon
          :icon="checking ? 'chevron-up' : 'chevron-down'"
          class="cordel-accordion-chevron"
        />

        <template v-slot:subtitle>
          Visualize ou edite as parcelas escolhidas
        </template>
      </typography-heading>

      <ul
        v-if="checking"
        class="customers-former-conditions"
      >
        <li class="customers-former-condition variant--empty">
          <label
            class="customers-former-condition-check"
            tabindex="0"
            :disabled="full"
            @click="checkAll"
            @keydown.enter="checkAll"
            @keydown.space="checkAll"
          >
            <font-awesome-icon
              class="customers-former-condition-icon"
              :icon="full ? 'square-check' : ['far', 'square']"
            />
          </label>

          <span class="customers-former-condition-info" />
        </li>

        <li
          v-for="condition in conditions"
          :key="condition.id"
          class="customers-former-condition"
        >
          <label
            class="customers-former-condition-check"
            tabindex="0"
            @click="check(condition.id)"
            @keydown.enter="check(condition.id)"
            @keydown.space="check(condition.id)"
          >
            <font-awesome-icon
              class="customers-former-condition-icon"
              :icon="selected[condition.id] ? 'square-check' : ['far', 'square']"
            />
          </label>

          <span class="customers-former-condition-info">
            {{ condition.installments }}x

            <b class="customers-former-condition-bold">
              {{ condition.value | monetize }}
            </b>

            <br class="customers-former-condition-break">

            + Entrada:

            <b class="customers-former-condition-bold">
              {{ condition.down | monetize }}
            </b>

            <span
              v-for="balloon in condition.balloons"
              :key="balloon.value"
              class="customers-former-condition-special"
            >
              + {{ balloon.months.length }}x

              <b class="customers-former-condition-bold">
                {{ balloon.value | monetize }}
              </b>

              <br class="customers-former-condition-break">

              a pagar {{ balloon.months.length > 1 ? 'nos meses' : 'no mês' }}:

              <b class="customers-former-condition-bold">
                {{ balloon.months.join(', ') }}
              </b>
            </span>
          </span>

          <span class="customers-former-condition-bank">
            {{ condition.bank }}
          </span>
        </li>
      </ul>

      <ul
        v-else
        class="customers-former-conditions-tags"
        @click="toggle"
      >
        <template v-if="banks.length">
          <li
            v-for="bank in banks"
            :key="bank.code"
            class="customers-former-conditions-tag"
          >
            <context-tag color="default">
              {{ bank.name }}
            </context-tag>
          </li>
        </template>

        <li
          v-else
          class="customers-former-conditions-tag"
        >
          <context-tag>
            Nenhuma condição selecionada
          </context-tag>
        </li>
      </ul>
    </section>

    <former-form
      v-if="customerState === 'ready'"
      ref="form"
      :button="button"
      :sections="sections"

      :loading="formerLoading"
      :error="formerError"
      :error-details="formerErrorDetails"

      @error="setFieldErrors"
      @change="setFieldValue"
      @edit="setField"
      @submit="submit"
    >
      <template
        v-if="(!bankCode || bankCodeForced) || (guarantor && guarantorSuccess)"
        v-slot:header
      >
        <former-alert
          v-if="guarantor && guarantorSuccess"
          type="success"
        >
          Cliente cadastrado com sucesso. Agora informe os dados do avalista.
        </former-alert>

        <ul
          v-if="false"
          class="customer-form-bank-wrapper"
        >
          <former-box
            v-if="!bankCode || bankCodeForced"
            key="personalization"
            title="Personalização do formulário"
            subtitle="Deseja que o formulário mostre apenas os campos de algum banco específico?"
          >
            <former-select
              class="proposals-form-bank-select"
              :field="field"
              :value="bankCode"
              inline
              @change="filter"
            />
          </former-box>
        </ul>
      </template>

      <template
        v-if="false && !guarantor"
        v-slot:footer
      >
        <div class="customers-former-footer">
          <former-button
            color="gray"
            center
            :loading="formerLoading"
            @click="submitAndNewGuarantor"
          >
            Salvar e adicionar avalista
          </former-button>
        </div>
      </template>
    </former-form>

    <!-- <customer-confirmation
      v-if="phoneConfirmation"
      :number="phoneConfirmation.number"
      @success="changedPhoneNumberSuccess"
      @close="changedPhoneNumberCancel"
    /> -->

    <popup-modal
      v-if="cnhOCRTimeoutCount"
      :closable="false"
      center
      small
      title="Processando dados..."
      @close="changedCNHFinally"
    >
      <img
        :src="cnhOCRLoading"
        alt="Carregando dados..."
        class="customers-former-illustration"
      >
    </popup-modal>

    <div id="credere" />
  </section>
</template>

<script>
  import Vue from 'vue';
  import { mapState } from 'vuex';

  import moment from 'moment';
  import 'moment/locale/pt-br';

  import namelize from '@sdk/helpers/formatters/namelize';
  import patternize, { depatternize } from '@sdk/helpers/formatters/patternize';

  import STATES from '@sdk/helpers/domains/states';
  import BANKS, { identify } from '@sdk/helpers/domains/banks';
  import unique from '@sdk/helpers/misc/unique';

  import cellphoneValidator from '@cordel/helpers/form/validators/cellphone';
  import invalidCnpj from '@cordel/helpers/form/validators/cnpj';
  import invalidCpf, { CPF_FORMAT } from '@cordel/helpers/form/validators/cpf';
  import invalidcpfOrCnpj from '@cordel/helpers/form/validators/cpf-or-cnpj';

  import { select } from '@/_CRUDL/helpers/form/fields';

  import { extractDate } from '@/helpers/filters/date';

  import { jsonReferrerIDs } from '@/helpers/apis/references';
  import scrollHelper from '@/helpers/views/scroll';
  import former from '@/helpers/mixins/former';
  import preload from '@/helpers/mixins/preload';
  import customersRead from '@/views/Customers/Form/mixin';

  import domains from '@/helpers/apis/domains';

  import {
    sectionsPF,
    sectionsPJ,
  } from '@/views/Customers/Form/sections';

  import cnhOCRLoading from '@/assets/images/illustrations/cnh/loading.gif';

  import FormerAlert from '@/components/Former/Alert.vue';
  import FormerButton from '@/components/Former/Button.vue';
  import FormerBox from '@/components/Former/Box.vue';
  import FormerForm from '@/components/Former/Form.vue';
  import FormerSelect from '@/components/Former/Field/Select.vue';

  import StateMessages from '@/components/State/Messages.vue';
  // import CustomerConfirmation from '@/views/Customers/Confirmation.vue';
  import PopupModal from '@/components/Popup/Modal.vue';

  import TypographyHeading from '@cordel/components/Typography/Heading.vue';
  import ContextTag from '@cordel/components/Context/Tag.vue';

  import customerMap, {
    nationalityMap,
    spouseMap,
    bankReferenceMap,
    mainAddressMap,
    jobReferenceAddressMap,
    previousAddressMap,
    alternativeAddressMap,
    jobReferenceMap,
    previousJobReferenceMap,
    cnhMap,
    accountantMap,
    emailMap,
    mainPhoneMap,
    attachmentMaps,
    monthlyInvoicingMap,
    cellPhoneMap,
  } from '@/views/Customers/Form/maps';

  import REQUIRED from './Form/banks';

  const BRADESCO = BANKS.BRADESCO.code;
  const BV = BANKS.BV.code;
  // const C6 = BANKS.C6.code;
  const CREDITAS = BANKS.CREDITAS.code;
  const FONTECRED = BANKS.FONTECRED.code;
  const HONDA = BANKS.HONDA.code;
  const ITAU = BANKS.ITAU.code;
  const PAN = BANKS.PAN.code;
  const SANTANDER = BANKS.SANTANDER.code;
  const KUNA = BANKS.KUNA.code;
  const MONEYPLUS = BANKS.MONEYPLUS.code;

  export default {
    name: 'CustomersNew',
    components: {
      FormerForm,
      FormerAlert,
      FormerBox,
      FormerButton,
      FormerSelect,

      // CustomerConfirmation,

      StateMessages,

      PopupModal,

      TypographyHeading,
      ContextTag,
    },
    mixins: [
      preload,
      former,
      customersRead,
    ],
    data() {
      return {
        field: {
          ...select,
          id: 'seller_ids',
          placeholder: 'Selecionar banco',
          options: [{
            label: 'Mostrar campos de todos os bancos',
            value: null,
          }].concat(
            Object.keys(BANKS).map(key => ({
              label: `Mostrar campos do ${BANKS[key].name}`,
              value: BANKS[key].code,
            })),
          ),
        },

        guarantorSuccess: false,
        guarantorProcess: false,

        phoneConfirmation: null,
        skipCustomerError: true,

        cnhOCRTimeout: null,
        cnhOCRTimeoutCount: null,
        cnhOCRLoading,

        isPJ: false,

        searchingCpfCnpj: null,

        sections: [],

        checked: {},
        checking: false,
      };
    },
    computed: {
      ...mapState([
        'zipFinder',
        'banksWeb',
        'bankValidations',
        'leads',
        'simulations',
        'ocr',
        'auth',
      ]),

      bankCode() {
        const {
          bank_code: code,
        } = this.$route.query;

        if (!code) {
          return null;
        }

        return String(code);
      },
      bankCodeForced() {
        return this.$route.query.bank_code_forced;
      },

      button() {
        const button = {
          icon: 'check',
        };

        const {
          simulation_uuid: simulationUUID,
          simulation_uuids: simulationUUIDs,
        } = this.$route.query;

        if (simulationUUID || simulationUUIDs) {
          button.label = 'Salvar proposta';
        } else {
          button.label = 'Salvar';
        }

        return button;
      },

      customer() {
        const {
          create,
          read,
          update,
        } = this.customers;

        if (create.item.id) {
          return create.item;
        }

        if (update.item.id) {
          return update.item;
        }

        return read.item;
      },
      guarantor() {
        return !!this.$route.query.guarantor;
      },

      customerState() {
        const {
          banksWeb,
          bankValidations,
          domainsWeb,
          customers,
          customer,
          skipCustomerError,
        } = this;

        if (banksWeb.list.loading || domainsWeb.list.loading || bankValidations.loading) {
          return 'loading';
        }

        const {
          customer_id: customerID,
          cpf_cnpj: cpfCnpj,
        } = this.$route.query;

        if (customerID || cpfCnpj) {
          return 'ready';
        }

        return (customers.read.loading || customer.id || skipCustomerError) ? 'ready' : 'error';
      },

      formerLoading() {
        const {
          create,
          update,
        } = this.customers;

        return create.loading || update.loading;
      },
      formerError() {
        const {
          create,
          update,
        } = this.customers;

        return create.error || update.error;
      },
      formerErrorDetails() {
        let details;

        try {
          details = this.formerError.message;
        } catch (err) { /* */ }

        return details;
      },

      conditions() {
        const {
          condition_id: conditionID,
          condition_ids: conditionIDs,
          condition_detail: conditionDetails,
        } = this.$route.query;

        const split = (v, f) => String(v || f || '').split(',').filter(i => i);

        const ids = split(conditionIDs || conditionID);
        const details = split(conditionDetails);

        if (details.length) {
          return ids
            .filter(id => id)
            .map((id, index) => {
              const [
                down,
                installments,
                value,
                balloons,
                code,
                choosen,
              ] = details[index].split('a-a');

              return {
                id: parseInt(id, 10),
                code,
                bank: identify(code),
                down: parseInt(down, 10),
                installments: parseInt(installments, 10),
                value: parseInt(value, 10),
                choosen: ['y', 'n'].includes(choosen) ? choosen === 'y' : undefined,
                balloons: balloons.split('b-b')
                  .filter(b => b)
                  .map((b) => {
                    const [
                      balloon,
                      months,
                    ] = b.split('c-c');

                    return {
                      value: parseInt(balloon, 10),
                      months: months.split('d-d'),
                    };
                  }),
              };
            });
        }

        return [];
      },
      selected() {
        if (!this.bankCode) {
          return {};
        }

        const bankCodes = this.bankCode.split(',');

        return this.conditions
          .reduce((reduced, condition) => {
            if (typeof this.checked[condition.id] === 'boolean') {
              reduced[condition.id] = this.checked[condition.id];
            } else {
              reduced[condition.id] = typeof condition.choosen === 'boolean' ? condition.choosen : bankCodes.includes(condition.code);
            }

            return reduced;
          }, {});
      },
      full() {
        const { selected } = this;

        return Object.keys(selected).length === Object.values(selected).filter(b => b).length;
      },
      months() {
        return this.conditions
          .filter(condition => this.selected[condition.id])
          .reduce((reduced, condition) => {
            const months = condition.installments + condition
              .balloons
              .map(balloon => balloon.months.length)
              .reduce((a, b) => (a + b), 0);

            if (!reduced[months]) {
              reduced[months] = [];
            }

            reduced[months].push(condition);

            return reduced;
          }, {});
      },
      banks() {
        return unique(
          Object.keys(this.selected)
            .filter(id => this.selected[id])
            .map((id) => {
              const { code } = this.conditions.find(c => c.id === parseInt(id, 10));

              return {
                code,
                name: identify(code),
              };
            }),
        );
      },
    },

    watch: {
      $route(to) {
        if (!to.params.id) {
          this.loadSettings();
        }
      },
    },

    mounted() {
      this.initialize();
    },

    beforeDestroy() {
      this.clearStores();
    },

    methods: {
      initialize() {
        this.sections = this.isPJ ? sectionsPJ() : sectionsPF();

        this.loadSettings();
        this.setFieldsCallback();

        this.configureBanks();
      },

      filter(bankCode) {
        this.$router.replace({
          query: {
            ...this.$route.query,

            bank_code: bankCode,
            bank_code_forced: true,
          },
        }).then(this.initialize);
      },

      clearStores() {
        return ['create', 'update', 'read']
          .forEach(action => this.$store.dispatch('customers/clear', { action }));
      },

      setCustomerType(isPJ = false, options = {}) {
        const {
          disabled = false,
          reset = false,
        } = options;

        this.setSections(isPJ, reset);

        this.setFieldValue(customerMap.INTERNAL_customer_type, !isPJ, {
          silent: true,
        });

        this.setFieldDisabled(customerMap.INTERNAL_customer_type, disabled);
      },

      setSections(bool = false, reset = false) {
        if (this.isPJ === bool && !reset) {
          return;
        }

        this.isPJ = bool;
        this.sections = bool ? sectionsPJ() : sectionsPF();

        this.setFieldsCallback();

        try {
          this.gotDomainsWeb();
          this.gotBanksWeb();
          this.gotAttachments();
        } catch (err) {
          this.getDomainsWeb();
          this.getBanksWeb();
        }
      },

      loadSettings() {
        this.skipCustomerError = true;

        const { query, params } = this.$route;
        const { customerID, guarantor, cpf_cnpj: number } = query;
        const cpfCnpj = depatternize(number);

        if (!params.id && !invalidcpfOrCnpj(cpfCnpj)) {
          this.setCustomerType(!invalidCnpj(cpfCnpj), {
            disabled: true,
          });
        } else if (invalidcpfOrCnpj(cpfCnpj) && customerID && guarantor) {
          this.setCustomerType(false, {
            reset: true,
          });
          this.setFieldSkip(customerMap.INTERNAL_customer_type, false);
        }

        if (this.getField(customerMap.cpf_cnpj)) {
          this.setFieldDisabled(customerMap.cpf_cnpj, !customerID && !guarantor);
        }

        Promise
          .all([
            this.getDomainsWeb(),
            this.getBanksWeb(),
          ])
          .then(() => this.getCustomer(params.id || cpfCnpj));
      },

      setFieldsCallback() {
        const {
          isPJ,
          setFieldCallback,
        } = this;

        const cb = setFieldCallback;

        const addressesMaps = [
          mainAddressMap,
          previousAddressMap,
          alternativeAddressMap,
        ];

        cb(customerMap.INTERNAL_customer_type, this.changedCustomerType, false);
        cb(customerMap.cpf_cnpj, this.changedCPCNPJ, false);
        cb(customerMap.has_made_funding, this.changedHasMadeFunding, true);

        const changedPhone = i => (value, old) => this.changedPhoneNumber(i, value, old);
        cb(customerMap.phones[0].number, changedPhone(0), false);
        cb(customerMap.phones[1].number, changedPhone(1), false);

        cb(mainAddressMap.set_time_year, this.changedAddressSince);

        if (!isPJ) {
          addressesMaps.push(jobReferenceAddressMap);

          cb(customerMap.have_credit_card, this.changedHaveCreditCard, true);
          cb(customerMap.has_cnh, this.changedHasCNH, true);
          cb(customerMap.born_at, this.changedBornAt);

          cb(customerMap.nationality, this.changedNationality, true);
          cb(customerMap.marital_status_id, this.changedMaritalStatus, true);
          cb(customerMap.have_bank_account, this.changedHaveBankAccount, true);
          cb(customerMap.document_type, this.changedDocumentType);

          cb(jobReferenceMap.joined_at, this.changedJobJoinedAt, true);
          cb(jobReferenceMap.first_job, this.changedFirstJob, true);

          cb(jobReferenceMap.profession_id, this.changedJobReference, true);
          cb(jobReferenceMap.professional_ocupation_id, this.changedJobReference, true);
          cb(jobReferenceMap.INTERNAL_still_works, this.changedStillWorks);

          cb(jobReferenceMap.another_income_cents, this.changedAnotherIncome, true);

          cb(
            customerMap.job_reference.INTERNAL_reply_customer_address,
            this.changedReplyCustomerAddress,
            true,
          );

          if (!this.$route.params.id) {
            this.setFieldSkip(customerMap.cnh_ocr_file, false);
            cb(customerMap.cnh_ocr_file, this.changedCNHFile);
          }
        } else {
          cb(customerMap.partners[0].marital_status_id, this.changedPartnerMaritalStatus, true);
        }

        addressesMaps.forEach((address) => {
          cb(address.zip_code, () => this.getZip(address));

          if (address.INTERNAL_present) {
            cb(address.INTERNAL_present, () => this.changedAddressPresent(address), true);
          }

          if (address.build_type_id) {
            cb(address.build_type_id, () => this.changedBuildType(address), true);
          }
        });
      },

      is(banks) {
        return !this.bankCode || this.bankCode.split(',').find(code => banks.includes(code));
      },

      getCustomer(identifier = null) {
        if ([null, undefined, ''].indexOf(identifier) > -1) {
          return null;
        }
        this.setFieldProcessingBulk(customerMap, true);

        const cleanIdentifier = depatternize(identifier);
        const isCpfCnpj = !invalidcpfOrCnpj(cleanIdentifier);

        if (isCpfCnpj) {
          this.setFieldValue(customerMap.cpf_cnpj, identifier, {
            silent: true,
          });
        }

        const params = isCpfCnpj ? {
          cpf_cnpj: cleanIdentifier,
        } : {
          id: identifier,
        };

        if (isCpfCnpj) {
          if (cleanIdentifier === (this.customer.cpf || this.customer.cnpj)) {
            this.gotCustomer();
            this.configureBanks();

            return Promise.resolve();
          }
        } else if (parseInt(cleanIdentifier, 10) === this.customer.id) {
          this.gotCustomer();
          this.configureBanks();

          return Promise.resolve();
        }

        this.clearStores();

        return this.$store
          .dispatch(`customers/${isCpfCnpj ? 'find' : 'read'}`, params)
          .then(this.gotCustomer)
          .catch(this.gotCustomerFailure)
          .finally(this.configureBanks);
      },

      gotCustomer() {
        this.searchingCpfCnpj = null;
        this.skipCustomerError = false;
        this.setFieldProcessingBulk(customerMap, false);

        const {
          cpf,
          cnpj,
          id,
        } = this.customer;

        if (!cpf && cnpj) {
          this.setCustomerType(true, {
            disabled: true,
          });
        }

        const {
          store_id: storeID,
          seller_id: sellerID,

          customer_id: customerID,
          guarantor,

          proposal_id: proposalID,
          proposal_attempt_id: proposalAttemptID,

          commercial,

          simulation_uuid: simulationUUID,
          simulation_uuids: simulationUUIDs,

          condition_ids: conditionIDs,
          condition_detail: conditionDetail,
        } = this.$route.query;

        const query = {
          store_id: storeID,
          seller_id: sellerID,

          customer_id: customerID,
          cpf: cpf || cnpj,
          cpf_cnpj: cpf || cnpj,
          guarantor,

          proposal_id: proposalID,
          proposal_attempt_id: proposalAttemptID,

          condition_ids: conditionIDs,
          condition_detail: conditionDetail,
        };

        if (simulationUUIDs) {
          query.simulation_uuids = simulationUUIDs;
        } else {
          query.simulation_uuid = simulationUUID;
        }

        if (commercial) {
          query.commercial = true;
        }

        if (this.bankCode) {
          query.bank_code = this.bankCode;

          if (this.bankCodeForced) {
            query.bank_code_forced = true;
          }
        }

        if (id) {
          this.$router.replace({
            name: 'customers-edit',
            params: {
              id,
            },
            query,
          }).then(() => {
            const {
              query: q,
              params: p,
            } = this.$route;

            this.setFieldDisabled(
              customerMap.cpf_cnpj, (!!q.guarantor && !!q.customer_id) || !!p.id,
            );
          });
        }

        this.setValues();
        this.setAttachmentsList();

        if (!this.isPJ) {
          this.changedCNHFinally();
        }
      },

      gotCustomerFailure() {
        this.setFieldProcessingBulk(customerMap, false);

        if (this.$route.params.id) {
          this.skipCustomerError = false;
        }

        this.getLead();
      },

      getLead() {
        const cpfCnpj = this.getFieldValue(customerMap.cpf_cnpj) || this.$route.query.cpf_cnpj;

        const {
          store_id: storeID,
        } = this.$route.query;

        const {
          item: simulation,
        } = this.simulations.read;

        if (depatternize(cpfCnpj) === (simulation.lead && depatternize(simulation.lead.cpf_cnpj))) {
          this.gotLead(simulation.lead);
        } else if ((this.skipCustomerError) && storeID) {
          this.$store
            .dispatch('leads/read', {
              header_store_id: storeID,
              cpf_cnpj: depatternize(cpfCnpj),
            })
            .then(this.gotLead)
            .catch(this.gotLeadFailure);
        }
      },

      gotLead(lead = {}) {
        this.searchingCpfCnpj = null;
        // this.setFieldProcessingBulk(customerMap, false);

        const {
          birthdate,
          name,
          gender,
          email,
          cpf_cnpj: cpfCnpj,
          monthly_income: monthlyIncome,
          mother_name: motherName,
          occupation,
          profession,
          address,
          phone_number: phoneNumber,
        } = lead.cpf_cnpj ? lead : this.leads.read.item;

        if (gender && this.domainsWeb.list.items.genre) {
          const genre = this.domainsWeb.list.items.genre.find(i => i.name === gender.label);
          if (genre) {
            this.setValue(customerMap.genre_id, genre.id);
          }
        }

        if (email) {
          this.setValue(customerMap.emails[0].address, email);
        }

        if (phoneNumber) {
          this.setValue(customerMap.phones[0].number, phoneNumber.replace('+55', ''));
        }

        this.setValue(customerMap.cpf_cnpj, cpfCnpj, { silent: true });
        this.setValue(customerMap.name, name);
        this.setValue(customerMap.born_at, birthdate);

        this.setValue(customerMap.mother, motherName);
        this.setValue(jobReferenceMap.income_cents, monthlyIncome);

        const zip = this.getFieldValue(mainAddressMap.zip_code);

        if (address && address.zip_code && depatternize(zip) !== depatternize(address.zip_code)) {
          this.setValue(mainAddressMap.zip_code, address.zip_code);
          this.setValue(mainAddressMap.number, address.number || '');
          this.setValue(mainAddressMap.complement, address.complement || '');
        }

        if (occupation) {
          const foundOccupation = this.domainsWeb.list.items.professional_ocupation
            .find(i => String(i.identifier) === String(occupation.credere_identifier));

          if (foundOccupation) {
            this.setValue(jobReferenceMap.professional_ocupation_id, foundOccupation.id);
          }
        }

        if (profession) {
          const foundProffesion = this.domainsWeb.list.items.profession
            .find(i => String(i.identifier) === String(profession.credere_identifier));

          if (foundProffesion) {
            this.setValue(jobReferenceMap.profession_id, foundProffesion.id);
          }
        }

        this.changedCNHFinally();
      },

      gotLeadFailure() {
        // this.setFieldProcessingBulk(customerMap, false);
      },

      configureBanks(map = { customer: customerMap }, required = { customer: REQUIRED }, key = 'customer') {
        if (!this.bankCode || !map[key] || !required[key] || this.isPJ) {
          return;
        }

        if (typeof map[key] === 'string') {
          const field = this.getField(map[key]);

          if (field && [undefined, null, ''].includes(this.getFieldValue(map[key]))) {
            if (!this.bankCode.split(',').some(bank => required[key].includes(bank))) {
              this.setFieldSkip(map[key], true);
            }
          }
        } else {
          Object.keys(map[key]).forEach(k => this.configureBanks(map[key], required[key], k));
        }
      },

      getZip(address) {
        const zip = this.getFieldValue(address.zip_code);

        if (!zip) {
          return;
        }

        this.setFieldProcessing(address.city, true);
        this.setFieldProcessing(address.neighborhood, true);
        this.setFieldProcessing(address.state_id, true);
        this.setFieldProcessing(address.street, true);

        this.$store
          .dispatch('zipFinder/find', { zip })
          .then(data => this.gotZip(address, data));
      },

      gotZip(address) {
        const {
          city,
          neighbourhood: neighborhood,
          state_id: stateID,
          street,
        } = this.zipFinder.result;

        this.setFieldValue(address.city, city);
        this.setFieldValue(address.neighborhood, neighborhood);
        this.setFieldValue(address.state_id, stateID);
        this.setFieldValue(address.street, street);

        this.setFieldProcessing(address.city, false);
        this.setFieldProcessing(address.neighborhood, false);
        this.setFieldProcessing(address.state_id, false);
        this.setFieldProcessing(address.street, false);
      },

      changedCustomerType() {
        this.setCustomerType(!this.getFieldValue(customerMap.INTERNAL_customer_type));

        this.setFieldDisabled(customerMap.cpf_cnpj, false);
        this.setFieldSkip(customerMap.INTERNAL_customer_type, false);
      },

      changedCPCNPJ() {
        const cpfCnpj = this.getFieldValue(customerMap.cpf_cnpj, { clean: true });

        if (this.searchingCpfCnpj === cpfCnpj) {
          return;
        }

        this.searchingCpfCnpj = cpfCnpj;

        if (!invalidcpfOrCnpj(cpfCnpj)) {
          this.skipCustomerError = true;
          this.getCustomer(cpfCnpj);

          if (!this.isPJ) {
            this.changedCNHFinally();
          }
        }
      },

      changedNationality() {
        const isBrasileiro = this.getFieldValue(customerMap.nationality);

        const included = this.is([
          BV,
          FONTECRED,
          HONDA,
          PAN,
          SANTANDER,
        ]);

        // (!isBrasileiro || !included) significa:
        // (se não for brasileiro) OU (se for brasileiro [implícito] e não estiver na lista)

        this.setFieldSkip(nationalityMap.place_of_birth, !isBrasileiro || !included);
        this.setFieldSkip(nationalityMap.state_of_birth_id, !isBrasileiro || !included);
      },

      changedMaritalStatus() {
        const {
          CASADO,
          VIVE_MARITALMENTE,
        } = domains.marital_status;

        const isMaried = [CASADO, VIVE_MARITALMENTE].includes(
          this.getFieldOption(customerMap.marital_status_id).label,
        );

        this.setFieldSkip(spouseMap.spouse_name, !isMaried || !this.is([
          BV,
          CREDITAS,
          FONTECRED,
          HONDA,
          ITAU,
          PAN,
          SANTANDER,
        ]));

        this.setFieldSkip(spouseMap.spouse_born_at, !isMaried || !this.is([
          BV,
          FONTECRED,
          HONDA,
          ITAU,
          PAN,
        ]));

        this.setFieldSkip(spouseMap.spouse_cpf, !isMaried || !this.is([
          BV,
          CREDITAS,
          FONTECRED,
          HONDA,
          ITAU,
          PAN,
          KUNA,
          MONEYPLUS,
          SANTANDER,
        ]));

        this.setFieldSkip(spouseMap.spouse_income_cents, true);
      },

      changedPartnerMaritalStatus() {
        const {
          CASADO,
          VIVE_MARITALMENTE,
        } = domains.marital_status;

        const isMaried = [CASADO, VIVE_MARITALMENTE].includes(
          this.getFieldOption(customerMap.partners[0].marital_status_id).label,
        );

        [
          'spouse_name',
          'spouse_born_at',
          'spouse_cpf',
        ].forEach(i => this.setFieldSkip(customerMap.partners[0][i], !isMaried));
      },

      changedHaveBankAccount() {
        const hasAccount = this.getFieldValue(customerMap.have_bank_account);

        this.setFieldSkip(bankReferenceMap.INTERNAL_bank_separator, !hasAccount);
        this.setFieldSkip(bankReferenceMap.bank_id, !hasAccount);
        this.setFieldSkip(bankReferenceMap.overdraft, !hasAccount);
        this.setFieldSkip(bankReferenceMap.agency, !hasAccount);
        this.setFieldSkip(bankReferenceMap.account_number, !hasAccount);
        this.setFieldSkip(bankReferenceMap.open_at, !hasAccount);
      },

      changedHaveCreditCard() {
        const hasCreditCard = this.getFieldValue(customerMap.have_credit_card);
        this.setFieldSkip(customerMap.credit_cards, !hasCreditCard);
      },

      changedHasMadeFunding() {
        const hasFunding = this.getFieldValue(customerMap.has_made_funding);

        this.setFieldSkip(customerMap.previous_funding_bank_id, !hasFunding || !this.is([
          HONDA,
        ]));
      },

      changedAddressPresent(address) {
        const present = this.getFieldValue(address.INTERNAL_present);

        Object.keys(address)
          .filter(key => key !== 'INTERNAL_present')
          .forEach(key => this.setFieldSkip(address[key], !present));
      },

      changedBuildType(address) {
        const {
          label: optionLabel,
        } = this.getFieldOption(address.build_type_id);

        const {
          FINANCIADA,
          ALUGADA,
        } = domains.build_types;

        let label = 'Aluguel/prestação';

        if (optionLabel === ALUGADA) {
          label = 'Aluguel';
        } else if (optionLabel === FINANCIADA) {
          label = 'Prestação';
        }

        this.setFieldLabel(address.rent_value_cents, label);
        this.setFieldSkip(address.rent_value_cents, ![
          FINANCIADA,
          ALUGADA,
        ].includes(optionLabel));
      },

      changedDocumentType() {
        const documentType = this.getFieldValue(customerMap.document_type);

        const isCNH = documentType === '1015';
        const isRG = documentType === 'RG';
        const isCTPS = documentType === 'CTPS';

        if (isCNH) {
          this.setFieldValue(cnhMap.has_cnh, true);
        } else {
          const hasCNHValue = !!this.getFieldValue(cnhMap.cnh)
                              || !!this.getFieldValue(cnhMap.cnh_type_id);

          this.setFieldValue(cnhMap.has_cnh, hasCNHValue ? true : null);
        }

        let skipIssuing = false;
        let skipState = false;

        if (this.bankCode) {
          const bankCodes = this.bankCode.split(',');
          const included = bank => bankCodes.includes(bank);

          if ([BV, PAN].find(included)) {
            skipIssuing = !isRG;
          }

          if ([CREDITAS].find(included)) {
            skipState = !isRG;
          }

          if ([FONTECRED, SANTANDER].find(included)) {
            skipIssuing = false;
            skipState = false;
          }
        }

        this.setFieldSkip(customerMap.rg_issuing, skipIssuing);
        this.setFieldSkip(customerMap.rg_state_id, skipState);

        this.setFieldSkip(customerMap.ctps_file, !isCTPS);

        this.setFieldSkip(customerMap.rg_front_file, !isRG);
        this.setFieldSkip(customerMap.rg_back_file, !isRG);

        this.setFieldSkip(customerMap.INTERNAL_cnh_file_separator, !isCNH);
        this.setFieldSkip(customerMap.cnh_file, !isCNH);
      },

      changedHasCNH() {
        const documentType = this.getFieldValue(customerMap.document_type);

        const isCNH = documentType === '1015';
        const hasCNH = this.getFieldValue(customerMap.has_cnh);

        this.setFieldSkip(cnhMap.cnh, isCNH ? true : !hasCNH);

        this.setFieldSkip(cnhMap.cnh_type_id, !hasCNH);
        this.setFieldSkip(customerMap.INTERNAL_cnh_file_separator, !hasCNH);
        this.setFieldSkip(customerMap.cnh_file, !hasCNH);
      },

      changedBornAt() {
        // const bornAt = this.getFieldValue(customerMap.born_at);
        // const minor = moment(bornAt, 'DD/MM/YYYY').isSameOrAfter(moment().subtract(18, 'year'));

        // this.setFieldSkip(cnhMap.has_cnh, minor);
      },

      changedJobJoinedAt() {
        const since = this.getFieldValue(jobReferenceMap.joined_at);
        const recent = this.is([BANKS.HONDA])
                       && moment(since, 'DD/MM/YYYY').isSameOrAfter(moment().subtract(1, 'year'));

        this.setFieldSkip(jobReferenceMap.first_job, recent);
        this.setFieldValue(jobReferenceMap.first_job, !recent);
      },

      changedFirstJob() {
        let firstJob = this.getFieldValue(jobReferenceMap.first_job);
        firstJob = firstJob === undefined ? true : firstJob;

        this.setFieldSkip(previousJobReferenceMap.previous_work, firstJob);
        this.setFieldSkip(previousJobReferenceMap.previous_work_start_at, firstJob);
        this.setFieldSkip(previousJobReferenceMap.previous_work_end_at, firstJob);
        this.setFieldSkip(previousJobReferenceMap.previous_job_phone, firstJob);
      },

      changedAddressSince() {
        const since = this.getFieldValue(mainAddressMap.set_time_year);
        const recent = this.is([HONDA])
                       && moment(since, 'DD/MM/YYYY').isSameOrAfter(moment().subtract(1, 'year'));

        this.setFieldSkip(previousAddressMap.INTERNAL_present, recent);

        if (recent) {
          this.setFieldValue(previousAddressMap.INTERNAL_present, true);
        }
      },

      changedJobReference() {
        const {
          label: occupation,
        } = this.getFieldOption(jobReferenceMap.professional_ocupation_id);

        const {
          label: profession,
        } = this.getFieldOption(jobReferenceMap.profession_id);

        const isAssalariado = occupation === domains.ocuppations.ASSALARIADO;
        const isAutonomo = occupation === domains.ocuppations.AUTONOMO;
        const isLiberal = occupation === domains.ocuppations.PROFESSIONAL_LIBERAL;
        const isEmpresario = occupation === domains.ocuppations.EMPRESARIO_SOCIO;
        const isAgropecuarista = occupation === domains.ocuppations.AGROPECUARISTA;
        const isAposentadoPodeTrabalhar = occupation === domains.ocuppations.APOSENTADO_PENSIONISTA;
        const isPublico = occupation === domains.ocuppations.SERV_PUB_ADM;
        const isAutarquia = occupation === domains.ocuppations.SERV_PUB_AUTARQUIA;
        const isAposentado = isAposentadoPodeTrabalhar
                             && profession === domains.professions.APOSENTADO;

        let skipName = true;
        let skipCNPJ = true;
        let skipJoinedAt = true;
        let skipProfession = false;
        let skipPhone = true;
        let skipAddress = true;
        let skipAccountant = true;
        let skipAccountanted = true;

        if (skipName && this.is([])) {
          skipName = isAposentado || (!isAssalariado && !isEmpresario);
        }

        if (skipName && this.is([
          BV,
          KUNA,
          MONEYPLUS,
        ])) {
          skipName = false;
        }

        if (skipName && this.is([
          BRADESCO,
          BV,
          FONTECRED,
          HONDA,
          PAN,
        ])) {
          skipName = isAposentado;
        }

        // else if (skipName && this.is([
        //   ITAU,
        // ])) {
        //   skipName = !isAssalariado && !isEmpresario;
        // }

        if (skipCNPJ && this.is([])) {
          skipCNPJ = !isAgropecuarista && !isEmpresario;
        }

        if (skipCNPJ && this.is([
          BRADESCO,
          FONTECRED,
          HONDA,
          PAN,
        ])) {
          skipCNPJ = !isAgropecuarista && !isEmpresario;
        }

        if (skipCNPJ && this.is([
          ITAU,
          BV,
        ])) {
          skipCNPJ = !isEmpresario;
        }

        if (skipJoinedAt && this.is([
          BRADESCO,
          BV,
          FONTECRED,
          HONDA,
        ])) {
          skipJoinedAt = isAposentado;
        }

        if (!skipProfession && this.bankCode === ITAU) {
          skipProfession = !isAssalariado && !isAutonomo && !isLiberal;
        }

        if (skipPhone && this.is([
          FONTECRED,
          HONDA,
        ])) {
          skipPhone = isAposentado;
        }

        if (skipPhone && this.is([
          ITAU,
        ])) {
          skipPhone = !isEmpresario;
        }

        if (skipPhone && this.is([
          PAN,
        ])) {
          skipPhone = !isAssalariado && !isEmpresario
                      && !isAutonomo && !isLiberal
                      && !isPublico && !isAutarquia;
        }

        if (skipPhone && this.is([
          BRADESCO,
        ])) {
          skipPhone = !isAssalariado && !isEmpresario
                      && !isAutonomo && !isLiberal
                      && !isPublico && !isAutarquia;
        }

        if (skipPhone && this.is([
          KUNA,
          MONEYPLUS,
        ])) {
          skipPhone = false;
        }

        if (skipAddress && this.is([
          FONTECRED,
          HONDA,
        ])) {
          skipAddress = isAposentado;
        }

        if (skipAddress && this.is([
          ITAU,
          BV,
        ])) {
          skipAddress = !isEmpresario;
        }

        if (skipAddress && this.is([
          PAN,
        ])) {
          skipAddress = !isAssalariado && !isEmpresario
                        && !isAutonomo && !isLiberal
                        && !isPublico && !isAutarquia;
        }

        if (skipAddress && this.is([
          BRADESCO,
        ])) {
          skipAddress = !isAssalariado && !isEmpresario
                        && !isAutonomo && !isLiberal
                        && !isPublico && !isAutarquia;
        }

        if (skipAccountant && this.is([
          HONDA,
        ])) {
          skipAccountant = !isAgropecuarista && !isEmpresario;
        }

        if (skipAccountanted && this.is([
          BRADESCO,
        ])) {
          skipAccountanted = !isEmpresario;
        }

        this.setFieldSkip(jobReferenceMap.name, skipName);
        this.setFieldSkip(jobReferenceMap.cnpj, skipCNPJ);
        this.setFieldSkip(jobReferenceMap.joined_at, skipJoinedAt);
        this.setFieldSkip(jobReferenceMap.profession_id, skipProfession);
        this.setFieldSkip(jobReferenceMap.phone, skipPhone);

        this.setFieldSkipBulk(jobReferenceMap.address, skipAddress);

        this.setFieldSkip(accountantMap.name, skipAccountant && skipAccountanted);
        this.setFieldSkip(accountantMap.city, skipAccountant);
        this.setFieldSkip(accountantMap.phone, skipAccountant && skipAccountanted);

        this.setFieldSkip(jobReferenceMap.department, isAposentado || !this.is([
          PAN,
        ]));

        this.setFieldSkip(jobReferenceMap.detail, !isAposentadoPodeTrabalhar || !this.is([
          BV,
          HONDA,
        ]));

        this.setFieldSkip(jobReferenceMap.company_activity_id, isAposentado || !this.is([
          HONDA,
        ]));

        this.setFieldSkip(jobReferenceMap.another_income_cents, !this.is([HONDA]));

        this.setFieldSkip(jobReferenceMap.INTERNAL_still_works, !isAposentadoPodeTrabalhar);

        this.setFieldSkip(jobReferenceMap.first_job, !this.is([HONDA]));
      },

      changedStillWorks() {
        const stillWorks = this.getFieldValue(jobReferenceMap.INTERNAL_still_works);

        const {
          label: occupation,
        } = this.getFieldOption(jobReferenceMap.professional_ocupation_id);

        const {
          label: profession,
        } = this.getFieldOption(jobReferenceMap.profession_id);

        if (occupation !== domains.ocuppations.APOSENTADO_PENSIONISTA) {
          return;
        }

        if (stillWorks) {
          if (profession === domains.professions.APOSENTADO) {
            this.setFieldValue(jobReferenceMap.profession_id, null);
          }
        } else {
          this.setFieldValue(
            jobReferenceMap.profession_id,
            this.domainsWeb.list.items.profession
              .find(item => item.name === domains.professions.APOSENTADO)
              .id,
          );
        }
      },

      changedAnotherIncome() {
        const {
          another_income_cents: cents,
          another_income_type_id: origin,
        } = jobReferenceMap;

        const income = this.getFieldValue(cents, { clean: true });
        this.setFieldSkip(origin, !income || income <= 0);
      },

      changedReplyCustomerAddress() {
        if (this.getFieldValue(jobReferenceMap.INTERNAL_reply_customer_address)) {
          Object
            .keys(jobReferenceMap.address)
            .forEach((key) => {
              this.setFieldValue(
                jobReferenceMap.address[key],
                this.getFieldValue(customerMap.address[key]), {
                  silent: true,
                },
              );
            });
        }
      },

      changedCNHFile() {
        const value = this.getFieldValue(customerMap.cnh_ocr_file);

        if (!value) {
          return;
        }

        let json;

        try {
          json = JSON.parse(value);
        } catch (err) { /* */ }

        if (!json || !json.key) {
          return;
        }

        this.setFieldValue(customerMap.cnh_file, value);

        clearTimeout(this.cnhOCRTimeout);
        this.cnhOCRTimeoutCount = 16;

        this.$store
          .dispatch('ocr/create', {
            file_key: json.key,
          })
          .then(this.changedCNHRead)
          .catch(this.changedCNHFailure);
      },

      changedCNHRead() {
        this.$store
          .dispatch('ocr/read', {
            id: this.ocr.create.item.token,
          })
          .then(this.changedCNHReadRetry)
          .catch(this.changedCNHFailure);
      },

      changedCNHReadRetry() {
        clearTimeout(this.cnhOCRTimeout);
        this.cnhOCRTimeoutCount = this.cnhOCRTimeoutCount - 1;

        if (this.cnhOCRTimeoutCount <= 0) {
          this.changedCNHFailure();

          return;
        }

        switch (this.ocr.read.item.status) {
          case 'PENDENTE':
            this.cnhOCRTimeout = setTimeout(this.changedCNHRead, 2 * 1000);

            break;
          case 'PROCESSADO':
            this.changedCNHSuccess();

            break;
          default:
            this.changedCNHFailure();
        }
      },

      changedCNHSuccess() {
        const {
          data_de_nascimento: birthdate,
          nome: name,
          cpf: unformattedCPF,
          // rg,
          // orgao_emissor_rg: rgIssuing,
          estado_emissao: cnhState,
          numero_registro: cnhNumber,
          data_de_emissao: cnhDate,
          nome_do_pai: father,
          nome_da_mae: mother,
          categoria,
        } = this.ocr.read.item;

        const existing = depatternize(this.getFieldValue(customerMap.cpf_cnpj));

        if (existing && existing !== depatternize(unformattedCPF)) {
          this.changedCNHFailure();

          return;
        }

        const {
          items: domainsWeb,
        } = this.domainsWeb.list;

        const cpf = patternize(unformattedCPF, { format: CPF_FORMAT });
        const state = STATES.find(s => s.value === cnhState) || {};

        const cnhStateID = (domainsWeb.states.find(t => t.name === state.label) || {}).id;
        const cnhCategoryID = (domainsWeb.cnh_type.find(t => t.name === categoria) || {}).id;
        const documentTypeID = (domainsWeb.document_type.find(t => t.id === '1015') || {}).id;

        const fill = (key, value) => {
          if (!this.getFieldValue(key)) {
            this.setFieldValue(key, value);
          }
        };

        fill(customerMap.cpf_cnpj, cpf);
        fill(customerMap.name, namelize(name));
        fill(customerMap.born_at, birthdate);

        this.setFieldValue(customerMap.document_type, documentTypeID);
        this.setFieldValue(customerMap.rg, cnhNumber);
        this.setFieldValue(customerMap.rg_issuing, 'Detran');
        this.setFieldValue(customerMap.rg_state_id, cnhStateID);
        this.setFieldValue(customerMap.rg_date, cnhDate);

        this.setFieldValue(customerMap.father, namelize(father));
        this.setFieldValue(customerMap.mother, namelize(mother));

        // this.setFieldSkip(customerMap.has_cnh, true);
        // this.setFieldValue(customerMap.has_cnh, true, { silent: true });

        this.setFieldValue(customerMap.cnh, cnhNumber);
        this.setFieldValue(customerMap.cnh_type_id, cnhCategoryID);

        this.changedCNHFinally();
      },

      changedCNHFailure() {
        console.log('\n### failure...');
        // console.log('--- read', JSON.parse(JSON.stringify(this.ocr.read.item)));
        // console.log('--- create', JSON.parse(JSON.stringify(this.ocr.create.item)));
        console.log('\n');

        this.changedCNHFinally();
      },

      changedCNHFinally() {
        clearTimeout(this.cnhOCRTimeout);
        this.cnhOCRTimeoutCount = null;

        this.setFieldSkip(customerMap.cnh_ocr_file, true);
      },

      changedPhoneNumber(index, value, old) {
        if (value !== old) {
          this.setFieldValidated(customerMap.phones[index].number, false);
          this.setFieldValue(customerMap.phones[index].phone_confirmation_id, null);
        } else if (this.getFieldValidated(customerMap.phones[index].number)) {
          return;
        }

        const number = this.getFieldValue(customerMap.phones[index].number);
        const cellphone = !cellphoneValidator(number);

        if (number && cellphone) {
          this.phoneConfirmation = {
            index,
            number,
          };
        }
      },

      changedPhoneNumberSuccess(result) {
        const {
          index,
        } = this.phoneConfirmation;

        this.setFieldValidated(customerMap.phones[index].number, 'Confirmado');
        this.setFieldValue(customerMap.phones[index].phone_confirmation_id, result.id);
      },

      changedPhoneNumberCancel() {
        this.phoneConfirmation = null;
      },

      // submit generators
      generatePhoneValues(id, type, confirmation = null) {
        const phone = this.getFieldValue(id, { clean: true }) || '';

        let confirmationID = null;

        if (confirmation) {
          confirmationID = this.getFieldValue(confirmation);
        }

        const code = phone.substring(0, 2) || null;
        const number = phone.substring(2) || null;

        return {
          code,
          number,
          phone_type_id: (code || number)
            ? this.domainsWeb.list.items.phone_type.find(t => t.name === type).id : null,
          phone_confirmation_id: confirmationID,
        };
      },

      generateAddressValues(map, type) {
        const address = Object.keys(map)
          .filter(key => !key.match(/^INTERNAL_/))
          .reduce((config, key) => {
            if (key === 'set_time_year') {
              const date = this.getFieldValue(map[key]);

              if (!date) {
                config.set_time_year = null;
                config.set_time_month = null;

                return config;
              }

              const {
                years,
                months,
              } = extractDate(date);

              config.set_time_year = years;
              config.set_time_month = months || 1;
            } else {
              config[key] = this.getFieldValue(map[key], {
                clean: ['rent_value_cents'].indexOf(key) > -1,
              });
            }

            return config;
          }, {
            address_type_id: this.getFieldValue(map.zip_code)
              && Array.isArray(this.domainsWeb.list.items.address_type)
                ? (this.domainsWeb.list.items.address_type.find(o => o.name === type) || {}).id
                : null,
          });

        if (map.INTERNAL_present && !this.getFieldValue(map.INTERNAL_present)) {
          // eslint-disable-next-line no-underscore-dangle
          address._destroy = true;
        }

        return address;
      },

      getJobReferenceFieldValues() {
        const defaultConfig = {
          integer: true,
        };

        const {
          COMERCIAL,
          EMPRESA_ANTERIOR,
        } = domains.phone_types;

        const data = {
          address: !this.getFieldValue(jobReferenceMap.address.zip_code) ? ({
            _destroy: true,
          }) : this.generateAddressValues(
            jobReferenceAddressMap,
            domains.address_types.COMERCIAL,
          ),
          joined_at: this.getFieldValue(jobReferenceMap.joined_at),
          income_cents: this.getFieldValue(jobReferenceMap.income_cents, defaultConfig),
          another_income_cents: this.getFieldValue(
            jobReferenceMap.another_income_cents,
            defaultConfig,
          ),
          another_income_type_id: this.getFieldValue(jobReferenceMap.another_income_type_id),
          detail: this.getFieldValue(jobReferenceMap.detail),
          first_job: this.getFieldValue(jobReferenceMap.first_job),
          professional_ocupation_id: this.getFieldValue(jobReferenceMap.professional_ocupation_id),
          profession_id: this.getFieldValue(jobReferenceMap.profession_id),
          department: this.getFieldValue(jobReferenceMap.department),
          name: this.getFieldValue(jobReferenceMap.name),
          cnpj: this.getFieldValue(jobReferenceMap.cnpj),
          company_activity_id: this.getFieldValue(jobReferenceMap.company_activity_id),
          phone: this.generatePhoneValues(jobReferenceMap.phone, COMERCIAL),
        };

        if (this.getFieldValue(jobReferenceMap.first_job)) {
          return data;
        }

        return {
          ...data,
          previous_work: this.getFieldValue(previousJobReferenceMap.previous_work),
          previous_work_start_at: this.getFieldValue(
            previousJobReferenceMap.previous_work_start_at,
          ),
          previous_work_end_at: this.getFieldValue(previousJobReferenceMap.previous_work_end_at),
          previous_job_phone: this.generatePhoneValues(
            previousJobReferenceMap.previous_job_phone, EMPRESA_ANTERIOR,
          ),
        };
      },

      getCNHFieldValues() {
        const isCNH = this.getFieldValue(customerMap.document_type) === '1015';

        if (isCNH || this.getFieldValue(customerMap.has_cnh)) {
          return {
            cnh: this.getFieldValue(isCNH ? customerMap.rg : customerMap.cnh),
            cnh_type_id: this.getFieldValue(customerMap.cnh_type_id),
          };
        }

        return null;
      },

      getMaritalStatusFieldValues() {
        const {
          CASADO,
          VIVE_MARITALMENTE,
        } = domains.marital_status;

        const {
          label,
        } = this.getFieldOption(customerMap.marital_status_id);

        if ([CASADO, VIVE_MARITALMENTE].indexOf(label) > -1) {
          return {
            spouse_name: this.getFieldValue(customerMap.spouse_name),
            spouse_born_at: this.getFieldValue(customerMap.spouse_born_at),
            spouse_cpf: this.getFieldValue(customerMap.spouse_cpf),
            spouse_income_cents: this.getFieldValue(customerMap.spouse_income_cents),
          };
        }

        return null;
      },

      getPartnerMaritalStatusFieldValues() {
        const {
          CASADO,
          VIVE_MARITALMENTE,
        } = domains.marital_status;

        const {
          label,
        } = this.getFieldOption(customerMap.partners[0].marital_status_id);

        if ([CASADO, VIVE_MARITALMENTE].indexOf(label) > -1) {
          return {
            spouse_name: this.getFieldValue(customerMap.partners[0].spouse_name),
            spouse_born_at: this.getFieldValue(customerMap.partners[0].spouse_born_at),
            spouse_cpf: this.getFieldValue(customerMap.partners[0].spouse_cpf),
          };
        }

        return null;
      },

      getAccountantFieldValues() {
        // const {
        //   label,
        // } = this.getFieldOption(jobReferenceMap.professional_ocupation_id);

        // const {
        //   AGROPECUARISTA,
        //   EMPRESARIO_SOCIO,
        // } = domains.ocuppations;

        // if ([AGROPECUARISTA, EMPRESARIO_SOCIO].indexOf(label) > -1 || this.isPJ) {
          return {
            name: this.getFieldValue(accountantMap.name),
            city: this.getFieldValue(accountantMap.city),
            phone: this.generatePhoneValues(accountantMap.phone, domains.phone_types.PRINCIPAL),
          };
        // }

        // return {};
      },

      getNationalityFieldValues() {
        if (this.getFieldValue(customerMap.nationality) !== false) {
          return {
            place_of_birth: this.getFieldValue(customerMap.place_of_birth),
            state_of_birth_id: this.getFieldValue(customerMap.state_of_birth_id),
          };
        }

        return {};
      },

      getEmailFieldValues() {
        return [{
          address: this.getFieldValue(emailMap.address),
        }];
      },

      getPhoneFieldValues() {
        const {
          CELULAR,
          PRINCIPAL,
        } = domains.phone_types;

        const cellphone = this.generatePhoneValues(
          cellPhoneMap.number,
          CELULAR,
          cellPhoneMap.phone_confirmation_id,
        );

        const alternative = this.generatePhoneValues(
          mainPhoneMap.number,
          PRINCIPAL,
          mainPhoneMap.phone_confirmation_id,
        );

        if (!cellphone.code && !cellphone.number && !alternative.code && !alternative.number) {
          return [];
        }

        return [cellphone, alternative];
      },

      getAddressFieldValues() {
        const {
          ANTERIOR,
          ALTERNATIVO,
        } = domains.address_types;

        return [
          this.generateAddressValues(previousAddressMap, ANTERIOR),
          this.generateAddressValues(alternativeAddressMap, ALTERNATIVO),
        ];
      },

      getPersonalReferencesValues() {
        // const map = customerMap.personal_references[0];

        // return [{
        //   name: this.getFieldValue(map.name),
        //   city: this.getFieldValue(map.city),
        //   phone: this.generatePhoneValues(map.phone, domains.phone_types.PRINCIPAL),
        //   relationship: this.getFieldOption(map.relationship).label,
        // }];

        const map = customerMap.personal_references[0];

        const {
          label: relationship,
        } = this.getFieldOption(map.relationship);

        const phone = this.generatePhoneValues(map.phone, domains.phone_types.PRINCIPAL);

        const reference = {
          name: this.getFieldValue(map.name),
          city: this.getFieldValue(map.city),
          phone,
          relationship,
        };

        if (
          !reference.name && !reference.city && !reference.relationship
          && !phone.code && !phone.number
        ) {
          return [];
        }

        return [reference];
      },

      getPartnersValues() {
        const partnerMap = customerMap.partners[0];

        const {
          label: roleLabel,
        } = this.getFieldOption(partnerMap.role);

        const json = {
          name: this.getFieldValue(partnerMap.name),
          role: roleLabel,
          quota: parseFloat(this.getFieldValue(partnerMap.quota, { integer: true }) / 100),

          born_at: this.getFieldValue(partnerMap.born_at),
          started_at: this.getFieldValue(partnerMap.started_at),
          income_cents: this.getFieldValue(partnerMap.income_cents, { integer: true }),

          marital_status_id: this.getFieldValue(customerMap.partners[0].marital_status_id),
          ...this.getPartnerMaritalStatusFieldValues(),
        };

        const value = this.getFieldValue(partnerMap.cpf_cnpj);

        if (!invalidCpf(value)) {
          return [{
            ...json,
            cpf: value,
          }];
        }

        return [{
          ...json,
          cnpj: value,
        }];
      },

      getBankRefencesFieldValues() {
        const map = customerMap.bank_references[0];
        const account = (this.getFieldValue(map.account_number) || '').split('-');

        const present = this.getFieldValue(customerMap.have_bank_account);

        const bankReference = [{
          bank_id: this.getFieldValue(map.bank_id),
          overdraft: this.getFieldValue(map.overdraft),
          agency: this.getFieldValue(map.agency),
          open_at: this.getFieldValue(map.open_at),
          account_number: account.shift(),
          digit: account.length ? account.pop() : '',
        }];

        if (!this.isPJ && !present) {
          // eslint-disable-next-line no-underscore-dangle
          bankReference[0]._destroy = true;
        }

        return bankReference;
      },

      getAttachmentsFieldValues() {
        const map = [
          ...attachmentMaps,
          customerMap.cnpj_file,
          customerMap.cs_file,
          customerMap.fat_file,
        ];

        return map
          .map(key => this.getField(key))
          .filter(f => !!f)
          .map((field) => {
            let value = {};

            try {
              value = JSON.parse(this.getFieldValue(field.id));
            } catch (err) { /* */ }


            const personType = this.domainsWeb.list.items.person_attachment_type
              .find(t => field && t.identifier === field.meta.type);

            const type = this.domainsWeb.list.items.attachment_type
              .find(t => field && t.identifier === field.meta.type);

            const found = personType || type;

            if (!found || !(value && value.key && value.name)) {
              return {
                _destroy: true,
              };
            }

            return {
              remote_file_id: value.key,
              original_filename: value.name,
              attachment_type_id: found.id,
            };
          });
      },

      getCustomerFieldValues() {
        const value = this.getFieldValue(customerMap.cpf_cnpj);

        return !this.isPJ ? {
          cpf: value,
        } : {
          cnpj: value,
        };
      },

      getMonthlyBillingFieldValues() {
        return {
          monthly_billings: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
            .map((i) => {
              const field = this.getField(`${monthlyInvoicingMap.billing_cents}-${i}`);

              return {
                billing_cents: depatternize(field.value),
                month: field.data.month,
                year: field.data.year,
              };
            }),
        };
      },

      getFieldValues() {
        const {
          customer,
        } = this;

        const {
          customer_id: customerID,
        } = this.$route.query;

        const {
          PRINCIPAL: ADDRESS_PRINCIPAL,
        } = domains.address_types;

        const hasBankAccount = this.getFieldValue(customerMap.have_bank_account);

        let json = {
          customer: {
            ...this.getCustomerFieldValues(),
            name: this.getFieldValue(customerMap.name),
            nickname: this.getFieldValue(customerMap.nickname),
            born_at: this.getFieldValue(customerMap.born_at),
            // eslint-disable-next-line no-nested-ternary
            have_bank_account: this.isPJ ? true : (hasBankAccount === null ? null : hasBankAccount),
            accountant: this.getAccountantFieldValues(),
            address: this.generateAddressValues(mainAddressMap, ADDRESS_PRINCIPAL),
            has_made_funding: this.getFieldValue(customerMap.has_made_funding),
            previous_funding_bank_id: this.getFieldValue(customerMap.previous_funding_bank_id),
            accept_boleto: this.getFieldValue(customerMap.accept_boleto),
            note: this.getFieldValue(customerMap.note),

            // 1 -> N
            emails: this.getEmailFieldValues(),
            phones: this.getPhoneFieldValues(),
            bank_references: this.getBankRefencesFieldValues(),
            addresses: this.getAddressFieldValues(),
            attachments: this.getAttachmentsFieldValues(),
          },
        };

        if (!this.isPJ) {
          const nationality = this.getFieldValue(customerMap.nationality);

          json = {
            ...json,
            customer: {
              ...json.customer,

              mother: this.getFieldValue(customerMap.mother),
              father: this.getFieldValue(customerMap.father),
              document_type: this.getFieldValue(customerMap.document_type),
              rg: this.getFieldValue(customerMap.rg),
              rg_date: this.getFieldValue(customerMap.rg_date),
              rg_state_id: this.getFieldValue(customerMap.rg_state_id),
              rg_issuing: this.getFieldValue(customerMap.rg_issuing),
              has_cnh: this.getFieldValue(customerMap.has_cnh),
              ...this.getCNHFieldValues(),

              marital_status_id: this.getFieldValue(customerMap.marital_status_id),
              ...this.getMaritalStatusFieldValues(),

              // eslint-disable-next-line no-nested-ternary
              nationality: nationality === null ? null : (nationality ? 'Brasileira' : 'Estrangeira'),
              ...this.getNationalityFieldValues(),

              genre_id: this.getFieldValue(customerMap.genre_id),
              education_id: this.getFieldValue(customerMap.education_id),
              property: this.getFieldValue(customerMap.property),
              public_person: this.getFieldValue(customerMap.public_person),
              job_reference: this.getJobReferenceFieldValues(),
              have_credit_card: this.getFieldValue(customerMap.have_credit_card),
              credit_cards: this.getFieldValue(customerMap.credit_cards),

              personal_references: this.getPersonalReferencesValues(),
            },
          };
        } else if (json.customer.cnpj) {
          json = {
            ...json,
            customer: {
              ...json.customer,
              ...this.getMonthlyBillingFieldValues(),


              partners: this.getPartnersValues(true),
              company_activity_id: this.getFieldValue(customerMap.company_activity_id),

              ie: this.getFieldValue(customerMap.ie),
              im: this.getFieldValue(customerMap.im),

              joint_stock_cents: this.getFieldValue(customerMap.joint_stock_cents, {
                integer: true,
              }),
            },
          };
        }

        if (customer.id) {
          json.id = customer.id;

          jsonReferrerIDs({
            customer: {
              ...json.customer,
              emails: [],
              phones: [],
              bank_references: [],
              addresses: [],
              personal_references: [],
              partners: [],
              attachments: [],
              monthly_billings: [],
            },
          }, { customer });

          json.customer.emails = this.referredEmails(json);
          json.customer.phones = this.referredPhones(json);
          json.customer.bank_references = this.referredBankReferences(json);
          json.customer.addresses = this.referredAddresses(json);

          if (customer.cpf) {
            json.customer.personal_references = this.referredPersonalReferences(json);
          } else {
            json.customer.partners = this.referredPartnersReferences(json);
          }

          json.customer.attachments = this.referredAttachments(json);

          if (json.customer.cnpj) {
            json.customer.monthly_billings = this.referredMonthlyBillings(json);
          }
        }

        if (customerID) {
          json.customer.customer_id = customerID;
        }

        if (this.bankCode) {
          json.bank_validations = {
            bank_codes: this.bankCode,
          };
        }

        return json;
      },

      referredMonthlyBillings(json) {
        jsonReferrerIDs(json.customer.monthly_billings, this.customer.monthly_billings);

        return json.customer.monthly_billings.concat(
          this.customer.monthly_billings
            .splice(12)
            .map(({ id }) => ({ id, _destroy: true })),
        );
      },

      referredAddresses(json) {
        const {
          customer,
        } = this;

        const {
          ANTERIOR,
          ALTERNATIVO,
        } = domains.address_types;

        const find = type => customer.addresses.find(a => a.address_type.name === type) || {};
        const previous = find(ANTERIOR);
        const alternative = find(ALTERNATIVO);

        jsonReferrerIDs(json.customer.addresses, [previous, alternative]);

        return json.customer.addresses.concat(
          customer.addresses
            .filter(({ id }) => [previous.id, alternative.id].indexOf(id) === -1)
            .map(({ id }) => ({ id, _destroy: true })),
        );
      },

      referredPhones(json) {
        const {
          customer,
        } = this;

        const {
          PRINCIPAL,
          CELULAR,
        } = domains.phone_types;

        const find = type => customer.phones.find(p => p.phone_type
          && p.phone_type.name === type) || {};

        const cellphone = find(CELULAR);
        const main = find(PRINCIPAL);

        jsonReferrerIDs(json.customer.phones, [cellphone, main]);

        return json.customer.phones.concat(
          customer.phones
            .filter(({ id }) => [cellphone.id, main.id].indexOf(id) === -1)
            .map(({ id }) => ({ id, _destroy: true })),
        );
      },

      referredBankReferences(json) {
        jsonReferrerIDs(json.customer.bank_references, [this.customer.bank_references[0]]);

        return json.customer.bank_references.concat(
          this.customer.bank_references
            .splice(1)
            .map(({ id }) => ({ id, _destroy: true })),
        );
      },

      referredEmails(json) {
        jsonReferrerIDs(json.customer.emails, [this.customer.emails[0]]);

        return json.customer.emails.concat(
          this.customer.emails
            .splice(1)
            .map(({ id }) => ({ id, _destroy: true })),
        );
      },

      referredPartnersReferences(json) {
        jsonReferrerIDs(json.customer.partners, [this.customer.partners[0]]);

        return json.customer.partners.concat(
          this.customer.partners
            .splice(1)
            .map(({ id }) => ({ id, _destroy: true })),
        );
      },

      referredPersonalReferences(json) {
        jsonReferrerIDs(json.customer.personal_references, [this.customer.personal_references[0]]);

        return json.customer.personal_references.concat(
          this.customer.personal_references
            .splice(1)
            .map(({ id }) => ({ id, _destroy: true })),
        );
      },

      referredAttachments(json) {
        const {
          customer,
          domainsWeb,
        } = this;

        const {
          RG_FRONT,
          RG_BACK,
          CNH,
          CTPS,
          ADDRESS,
          INCOME,
        } = domains.person_attachment_types;

        const {
          FAT,
          CNPJ,
          CS,
        } = domains.attachment_types;

        const findPerson = type => domainsWeb.list.items.person_attachment_type
          .find(t => t.identifier === type);

        const find = type => domainsWeb.list.items.attachment_type
          .find(t => t.identifier === type);

        const findAttachment = type => customer.attachments
          .find(a => a.attachment_type_id === {
            ...(findPerson(type) || find(type)),
          }.id) || {};

        const fileFAT = findAttachment(FAT.type);
        const fileCNPJ = findAttachment(CNPJ.type);
        const fileCS = findAttachment(CS.type);

        const fileRGFront = findAttachment(RG_FRONT.type);
        const fileRGBack = findAttachment(RG_BACK.type);
        const fileCNH = findAttachment(CNH.type);
        const fileCTPS = findAttachment(CTPS.type);
        const fileAddress = findAttachment(ADDRESS.type);
        const fileIncome = findAttachment(INCOME.type);

        let all = [
          fileRGFront,
          fileRGBack,
          fileCNH,
          fileCTPS,
          fileAddress,
          fileIncome,
        ];

        if (this.isPJ) {
          all = [
            fileCS,
            fileCNPJ,
            fileFAT,
          ];
        }

        const allIDs = all.map(({ id }) => id);

        jsonReferrerIDs(json.customer.attachments, all);

        return json.customer.attachments.concat(
          customer.attachments
            .filter(({ id }) => allIDs.indexOf(id) === -1)
            .map(({ id }) => ({ id, _destroy: true })),
        );
      },

      submitAndNewGuarantor() {
        this.guarantorProcess = true;
        this.submit();
      },

      submit() {
        this.guarantorSuccess = false;
        this.$store
          .dispatch(`customers/${this.customer.id ? 'update' : 'create'}`, this.getFieldValues())
          .then(this.submitSuccess)
          .catch(this.submitFailure);
      },

      submitSuccess() {
        const {
          store_id: storeID,
          seller_id: sellerID,

          customer_id: customerID,
          guarantor,

          proposal_id: proposalID,
          proposal_attempt_id: proposalAttemptID,

          commercial,

          simulation_uuid: simulationUUID,
          simulation_uuids: simulationUUIDs,

          condition_ids: conditionIDs,
          condition_detail: conditionDetail,
        } = this.$route.query;

        const query = {
          store_id: storeID,
          seller_id: sellerID,

          integrated: false,
        };

        if (simulationUUIDs) {
          query.simulation_uuids = simulationUUIDs;
        } else {
          query.simulation_uuid = simulationUUID;
        }

        const selected = Object.keys(this.selected).filter(id => this.selected[id]);
        query.condition_ids = selected.length ? selected.join(',') : conditionIDs;

        if (conditionDetail) {
          query.condition_detail = conditionDetail;
        }

        if (commercial) {
          query.commercial = true;
        }

        if (proposalID) {
          query.proposal_id = proposalID;
        }

        if (proposalAttemptID) {
          query.proposal_attempt_id = proposalAttemptID;
        }

        if (this.bankCode) {
          query.bank_code = this.bankCode;
        }

        if (this.guarantorProcess) {
          this.guarantorSuccess = true;
          this.guarantorProcess = false;

          if (this.getField(customerMap.cnh_ocr_file)) {
            this.setFieldSkip(customerMap.cnh_ocr_file, false);
          }

          scrollHelper();

          return this.$router.replace({
            name: 'customers-new',
            query: {
              ...query,

              customer_id: this.customer.id,
              guarantor: true,
            },
          });
        }

        if (proposalAttemptID) {
          return this.$router.replace({
            name: 'proposal-attempts-edit',
            params: {
              proposal_id: proposalID,
              proposal_attempt_id: proposalAttemptID,
            },
            query: {
              ...query,
              customer_id: guarantor && customerID ? customerID : this.customer.id,
            },
          });
        }

        if (proposalID) {
          return this.$router.replace({
            name: 'proposal-attempts-new',
            params: {
              proposal_id: proposalID,
            },
            query: {
              ...query,
              customer_id: guarantor && customerID ? customerID : this.customer.id,
            },
          });
        }

        if (simulationUUID || simulationUUIDs) {
          return this.$router.replace({
            name: 'proposals-new',
            query: {
              ...query,
              customer_id: guarantor && customerID ? customerID : this.customer.id,
            },
          });
        }

        return this.$router.replace({
          name: 'customers-read-informations',
          params: {
            id: guarantor && customerID ? customerID : this.customer.id,
          },
        });
      },

      submitFailure(e) {
        console.log(e);

        this.guarantorSuccess = false;
        this.guarantorProcess = false;

        this.setErrors(customerMap, this.handle1toN(this.formerError), {
          unskip: true,
        });

        scrollHelper();
      },

      handle1toN(errors) {
        if (!errors) {
          return null;
        }

        const { ANTERIOR, ALTERNATIVO } = domains.address_types;
        const addresses = (errors.details && errors.details.addresses) || [];
        const types = this.domainsWeb.list.items.address_type || [];

        const address = (label) => {
          const {
            id,
          } = types.find(type => type.name === label);

          return addresses.find(error => error.object && error.object.address_type_id === id) || {};
        };

        return {
          ...errors,
          details: {
            ...errors.details,
            addresses: [
              address(ANTERIOR),
              address(ALTERNATIVO),
            ],
          },
        };
      },

      checkAll() {
        Object
          .keys(this.selected)
          .filter(id => !this.selected[id])
          .forEach(id => this.single(id, true));

        this.reload();
      },
      check(id) {
        this.single(id);

        this.reload();
      },
      uncheckable(id) {
        return this.selected[id] && Object.values(this.selected).filter(i => i).length <= 1;
      },
      single(id, force) {
        if (this.uncheckable(id)) {
          this.$store.dispatch('toasts/create', {
            color: 'warning',
            message: 'Pelo menos uma parcela precisa estar selecionada.',
          });

          return;
        }

        const bool = typeof force === 'boolean' ? force : !this.selected[id];

        if (typeof this.checked[id] === 'boolean') {
          this.checked[id] = bool;
        } else {
          Vue.set(this.checked, id, bool);
        }
      },

      toggle() {
        this.checking = !this.checking;
      },
      reload() {
        let { condition_detail: details } = this.$route.query;
        details = String(details || '').split(',');

        const codes = [];
        const map = (detail, index) => {
          const splitted = detail.split('a-a');
          const selected = this.selected[this.conditions[index].id];

          splitted[5] = selected ? 'y' : 'n';

          if (selected) {
            codes.push(this.conditions[index].code);
          }

          return splitted.join('a-a');
        };

        const detailed = details.map(map).join(',');

        this.$router.replace({
          query: {
            ...this.$route.query,
            condition_detail: detailed,
            bank_code: codes.join(','),
          },
        }).then(this.initialize);
      },
    },
  };
</script>

<style lang="scss">
  .former-error + .former-header .former-alert {
    display: none;
  }

  .customers-former-conditions-header {
    .cordel-typography-title {
      align-items: center;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }
  }
</style>

<style lang="scss" scoped>
  @import '@/assets/stylesheets/_variables.scss';

  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .customers-former-footer {
    order: 1;
    width: 100%;
    display: flex;
    margin: 0 0 $gutter-small 0;
    justify-content: flex-end;
    align-items: center;

    .former-button {
      flex: 1;
    }

    @media all and (min-width: $breakpoint-desktop) {
      margin: 0 $gutter-small 0 0;
      width: auto;
      order: 0;

      .former-button {
        flex: inherit;
      }
    }
  }

  .customers-former-illustration {
    display: block;
    margin: 0 auto;
    padding: 0 0 $gutter-big;
    width: auto;
  }

  .customer-form-bank-wrapper {
    border-bottom: $spacing solid $gray-light;
    list-style: none;
    padding-bottom: $gutter;
  }

  .proposals-form-bank-select {
    max-width: auto;

    @media all and (min-width: $breakpoint-desktop) {
      max-width: calc(50% - #{$gutter});
    }
  }

  .customers-former-conditions-container {
    border: 0 solid var(--cordel-color-constant-dark);
    border-width: var(--cordel-border-width-thin) 0;
    background-color: var(--cordel-color-neutral-lighter);
  }

  .customers-former-conditions-header {
    cursor: pointer;
    padding: var(--cordel-spacing-xl) var(--cordel-spacing-blaster) 0;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-spacing-xl) var(--cordel-spacing-s) 0;
    }
  }

  .customers-former-conditions {
    padding: var(--cordel-spacing-xl) var(--cordel-spacing-blaster) var(--cordel-spacing-giga);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding-left: var(--cordel-spacing-s);
      padding-right: var(--cordel-spacing-s);
    }
  }

  .customers-former-condition {
    align-items: stretch;
    background-color: var(--cordel-color-constant-light);
    border: var(--cordel-border-width-soft) solid var(--cordel-color-neutral-light);
    color: var(--cordel-color-constant-dark);
    display: flex;
    flex-direction: row;
    justify-content: center;
    line-height: var(--cordel-line-height-m);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      font-size: var(--cordel-font-size-s);
    }

    & + & {
      border-top: none;
    }
  }

  .customers-former-condition-check {
    align-items: flex-start;
    border-right: var(--cordel-border-width-soft) solid var(--cordel-color-neutral-light);
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: center;
    line-height: var(--cordel-line-height-m);
    padding: var(--cordel-spacing-xs);
    position: relative;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-spacing-xxxs);
    }

    &:hover {
      background-color: var(--cordel-color-neutral-lighter);
    }

    &:focus {
      position: relative;
      box-shadow: 0 0 0 var(--cordel-border-width-thin) var(--cordel-color-highlight-primary);
    }

    &[disabled] {
      cursor: default;

      &:hover,
      &:focus {
        background-color: transparent;
        box-shadow: none;
      }
    }
  }

  .customers-former-condition-icon {
    color: var(--cordel-color-neutral-primary);
    font-size: var(--cordel-font-size-xl);
    margin-top: 0.05em;
    transition: color .1s;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      font-size: var(--cordel-font-size-l);
    }

    &.fa-square-check {
      color: var(--cordel-color-constant-dark);
    }

    .customers-former-condition-check[disabled] & {
      color: var(--cordel-color-neutral-light);
    }
  }

  .customers-former-condition-info {
    flex: 1;
    padding: var(--cordel-spacing-xs) var(--cordel-spacing-xxxs);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-spacing-xxxs);
    }

    .variant--empty & {
      background-color: var(--cordel-color-neutral-lighter);
    }
  }

  .customers-former-condition-bank {
    align-items: center;
    display: flex;
    flex-direction: row;
    font-weight: var(--cordel-font-weight-bold);
    justify-content: center;
    padding: var(--cordel-spacing-xs) var(--cordel-spacing-giga) var(--cordel-spacing-xxxs) 0;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-spacing-xxxs) var(--cordel-spacing-xxxs) var(--cordel-spacing-xxxs) 0;
    }
  }

  .customers-former-condition-special {
    display: block;
    margin-top: var(--cordel-spacing-nano);
  }

  .customers-former-condition-bold {
    font-weight: var(--cordel-font-weight-bold);
    display: inline-block;
    word-wrap: nowrap;
  }

  .customers-former-condition-break {
    display: none;

    @media all and (max-width: $cordel-breakpoint-mobile) {
      display: block;
    }
  }

  .customers-former-conditions-tags {
    align-items: center;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    list-style: none;
    padding: var(--cordel-spacing-xxxs) var(--cordel-spacing-blaster) var(--cordel-spacing-xl);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding-left: var(--cordel-spacing-s);
      padding-right: var(--cordel-spacing-s);
    }
  }

  .customers-former-conditions-tag {
    & + & {
      margin-left: var(--cordel-spacing-nano);
    }
  }
</style>
